// Story 10.3 — Lista de conversaciones del bot. Sin virtualización por ahora
// (YAGNI: la mayoría de bots tendrá < 50). Si algún bot crece >200, agregar
// `@tanstack/react-virtual`.

import { Loader2 } from 'lucide-react';
import { type ConversationSummary, deriveDisplayName } from '@/lib/conversations-types';
import { formatRelative } from '@/lib/relative-time';
import { cn } from '@/lib/utils';

interface Props {
  conversations: ConversationSummary[];
  selectedSessionId: number | null;
  onSelect: (sessionId: number) => void;
  isLoading: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  isFetchingMore: boolean;
}

const SKELETON_KEYS = ['s1', 's2', 's3', 's4', 's5', 's6'] as const;

const STATUS_BADGE: Record<string, string> = {
  ACTIVE: 'bg-success/15 text-success',
  EXPIRED: 'bg-muted text-muted-foreground',
  COMPLETED: 'bg-primary/15 text-primary',
};

export function ConversationList({
  conversations,
  selectedSessionId,
  onSelect,
  isLoading,
  hasNextPage,
  onLoadMore,
  isFetchingMore,
}: Props): React.ReactElement {
  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {SKELETON_KEYS.map((k) => (
          <div key={k} className="h-16 animate-pulse rounded-md border border-border bg-muted/50" />
        ))}
      </div>
    );
  }

  return (
    <ul className="divide-y divide-border">
      {conversations.map((c) => {
        const display = deriveDisplayName(c);
        const isSelected = c.sessionId === selectedSessionId;
        return (
          <li key={c.sessionId}>
            <button
              type="button"
              onClick={() => onSelect(c.sessionId)}
              className={cn(
                'w-full px-3 py-3 text-left transition-colors hover:bg-muted/50',
                isSelected && 'bg-muted',
              )}
              data-session-id={c.sessionId}
              aria-current={isSelected ? 'true' : undefined}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{display.primary}</p>
                  {display.secondary && (
                    <p className="truncate font-mono text-xs text-muted-foreground">
                      {display.secondary}
                    </p>
                  )}
                </div>
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase',
                    STATUS_BADGE[c.status] ?? 'bg-muted text-muted-foreground',
                  )}
                >
                  {c.status}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>{c.flowId ?? 'sin flow'}</span>
                <span>
                  {c.messageCount} msg
                  {c.lastMessageAt && ` · ${formatRelative(c.lastMessageAt)}`}
                </span>
              </div>
            </button>
          </li>
        );
      })}
      {hasNextPage && (
        <li className="p-2">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isFetchingMore}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-border py-2 text-xs text-muted-foreground transition hover:bg-muted/50 disabled:opacity-50"
          >
            {isFetchingMore && <Loader2 className="h-3 w-3 animate-spin" />}
            Cargar más
          </button>
        </li>
      )}
    </ul>
  );
}
